import {
  IChargeUnit,
  ILoadTime,
  ILoadTimeSum,
  IMonthNum,
  IPVCharge,
  ISeasonMapping,
  ISeasonalCharge,
  ISeasonalNum,
  ITimeDataItem,
  ITouPeriods,
  TypeSeason,
} from './algoInterface'
import { calLoadTimeSum, calMonthSum } from './classifyTimeData'

/**
 * 요금 계산 - 부하별 요금
 * @param {ILoadTime} loadTime - 최저/중간/최대부하 합
 * @param {ILoadTime} chargeUnit - 한개 계절의 최저/중간/최대부하 요금단가
 * @returns {ISeasonalCharge} 부하별 요금
 */
const multiplyChargeValues = (
  loadTimeSum: ILoadTime,
  chargeUnit: ILoadTime
): ILoadTime => {
  return {
    min: loadTimeSum.min * chargeUnit.min,
    mid: loadTimeSum.mid * chargeUnit.mid,
    max: loadTimeSum.max * chargeUnit.max,
  }
}

/**
 * 월별 부하별 요금 계산
 * @param {ILoadTimeSum} loadTimeSum - 월별 최저/중간/최대부하 합
 * @param {IChargeUnit} chargeUnit - 계절별 최저/중간/최대부하 요금단가
 * @param {ISeasonMapping} seasonMapping - 월별 계절 정보
 * @returns {ILoadTimeSum} 월별 부하별 요금
 */
export const calMonthCharge = (
  loadTimeSum: ILoadTimeSum,
  chargeUnit: IChargeUnit,
  seasonMapping: ISeasonMapping
): ILoadTimeSum => {
  // 월별 부하별 요금 계산
  let monthLoadTimeCharge: ILoadTimeSum = {}

  for (let index = 1; index < 13; index++) {
    const season = seasonMapping[index] as TypeSeason
    monthLoadTimeCharge[index] = multiplyChargeValues(
      loadTimeSum[index],
      chargeUnit[season]
    )
  }

  return monthLoadTimeCharge
}

/**
 * 월별 부하별 요금 더해서 월별 요금 계산
 * @param {ILoadTimeSum} loadTimeSum 월별 부하별 요금
 * @return {IMonthNum} 월별 요금
 */
export const sumMonthlyLoadTime = (loadTimeSum: ILoadTimeSum): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  for (let month = 1; month < 13; month++) {
    //@ts-ignore
    result[month] =
      loadTimeSum[month].min + loadTimeSum[month].mid + loadTimeSum[month].max
  }

  return result
}

/**
 * 계절별 부하별 요금 더해서 계절별 요금 계산
 * @param {ILoadTimeSum} loadTimeSum 계절별 부하별 요금
 * @return {ISeasonalNum} 계절별 요금
 */
export const calSeasonLoadTimeToSum = (
  loadTimeSum: ISeasonalCharge
): ISeasonalNum => {
  const result: ISeasonalNum = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  for (let season of ['spr', 'sum', 'fall', 'win']) {
    //@ts-ignore
    result[season] =
      //@ts-ignore
      loadTimeSum[season].min +
      //@ts-ignore
      loadTimeSum[season].mid +
      //@ts-ignore
      loadTimeSum[season].max
  }

  return result
}

/**
 * 월별 요금 계산
 * @param {ITimeDataItem[]} timeData 사용량
 * @param {ITouPeriods} touPeriods 시간대
 * @param {IChargeUnit} chargeUnit 계절별 요금
 * @param {ISeasonMapping} seasonMapping - 월별 계절
 * @param {number[]} holidayData - 공휴일
 * @return {} 월별요금
 */
export const calMonthlyChargeAsUsage = (
  timeData: ITimeDataItem[],
  touPeriods: ITouPeriods,
  chargeUnit: IChargeUnit,
  seasonMapping: ISeasonMapping,
  holidayData: number[]
): IMonthNum => {
  // 월별 최저 중간 최대부하 합
  const monthlyLoadTimeSum = calLoadTimeSum(
    timeData,
    seasonMapping,
    holidayData,
    touPeriods
  ).loadTimeSumResult

  // 월별 부하별 요금 계산
  const monthlyLoadTimeCharge = calMonthCharge(
    monthlyLoadTimeSum,
    chargeUnit,
    seasonMapping
  )

  // 부하별 요금 합치기
  const monthlyCharge = sumMonthlyLoadTime(monthlyLoadTimeCharge)

  return monthlyCharge
}

/**
 * 월별 요금으로 총 요금 계산
 */
export const sumMonthNumToTotalNum = (monthNum: IMonthNum): number => {
  return (
    monthNum[1] +
    monthNum[2] +
    monthNum[3] +
    monthNum[4] +
    monthNum[5] +
    monthNum[6] +
    monthNum[7] +
    monthNum[8] +
    monthNum[9] +
    monthNum[10] +
    monthNum[11] +
    monthNum[12]
  )
}

/**
 * 계산한 고객별 요금 합산하기
 */
export const sumTotalPVCharge = (PVChargeList: IPVCharge[]): IPVCharge => {
  const result: IPVCharge = {
    pv: 0,
    charge: 0,
  }

  for (let index = 0; index < PVChargeList.length; index++) {
    result.pv += PVChargeList[index].pv
    result.charge += PVChargeList[index].charge
  }

  return result
}

/**
 * 한전 월별 요금 계산
 * @param {ITimeDataItem[]} timeData 사용량
 * @param {ITouPeriods} touPeriods 시간대
 * @param {IChargeUnit} chargeUnit 계절별 요금
 * @param {ISeasonMapping} seasonMapping - 월별 계절
 * @param {number[]} holidayData - 공휴일
 * @return {} 월별요금
 */
export const calKepcoMonthlyChargeAsUsage = (
  timeData: ITimeDataItem[],
  touPeriods: ITouPeriods,
  chargeUnit: IChargeUnit,
  seasonMapping: ISeasonMapping,
  holidayData: number[],
  defaultChargeUnit: number,
  KEPCOContractedPower: number,
  environmentalUnit: number = 0
): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  const usageCharge = calMonthlyChargeAsUsage(
    timeData,
    touPeriods,
    chargeUnit,
    seasonMapping,
    holidayData
  )

  const defaultCharge = defaultChargeUnit * KEPCOContractedPower
  const monthlyUsage = calMonthSum(timeData)

  for (let index: number = 1; index < 13; index++) {
    const month = index as keyof IMonthNum
    result[month] =
      usageCharge[month] + defaultCharge + environmentalUnit * monthlyUsage[month]
  }

  return result
}
